/**
 * Returns true when every flag in the array is enabled for the user.
 * An empty or nullish array means the item is ungated.
 */
function hasAllFeatureFlags(
  hasFeature: (name: string) => boolean,
  featureFlags?: string[] | null
): boolean {
  if (!featureFlags || featureFlags.length === 0) return true;
  return featureFlags.every((flag) => hasFeature(flag));
}

/**
 * Returns true when at least one flag in the array is enabled for the user.
 * An empty or nullish array means the item is ungated.
 */
function hasAnyFeatureFlag(
  hasFeature: (name: string) => boolean,
  featureFlags?: string[] | null
): boolean {
  if (!featureFlags || featureFlags.length === 0) return true;
  return featureFlags.some((flag) => hasFeature(flag));
}

/**
 * Filters out dashboards that have no enabled flag in their `featureFlags`
 * array (ANY-of semantics — the dashboard is visible while at least one
 * listed flag is enabled).
 *
 * Dashboards without `featureFlags` (nullish/empty) always pass through.
 *
 * Note this is intentionally looser than {@link filterWidgetsByFeature}'s
 * ALL-of semantics: every dashboard tag today is a single flag (identical
 * result under either semantics), but a multi-flag dashboard tag means
 * "this dashboard is meaningful while any of these features exist" — e.g.
 * `system-visualization` is tagged `["XDA Widgets", "3DV Widgets"]` by the
 * eva-workspace-svc seed, and should only disappear once *both* are off.
 */
export function filterDashboardsByFeature<T extends { featureFlags?: string[] | null }>(
  hasFeature: (name: string) => boolean,
  dashboards: T[]
): T[] {
  return dashboards.filter((dashboard) =>
    hasAnyFeatureFlag(hasFeature, dashboard.featureFlags)
  );
}

/**
 * Filters out widgets whose `featureFlags` array contains any flag the user
 * does not have enabled (ALL-of semantics — every listed flag must be
 * enabled for the widget to remain).
 *
 * Widgets without `featureFlags` (nullish/empty) always pass through.
 *
 * Note this is intentionally stricter than {@link filterDashboardsByFeature}'s
 * ANY-of semantics — see that function's doc for the rationale.
 */
export function filterWidgetsByFeature<T extends { featureFlags?: string[] | null }>(
  hasFeature: (name: string) => boolean,
  widgets: T[]
): T[] {
  return widgets.filter((widget) => hasAllFeatureFlags(hasFeature, widget.featureFlags));
}
